import { useEffect, useState } from 'react';
import type { HeroBannerItem } from '@/lib/heroBanners';

const ALIGN_CLASS: Record<string, string> = {
  left: 'items-start text-left mr-auto',
  center: 'items-center text-center mx-auto',
  right: 'items-end text-right ml-auto',
};

const ALIGN_PADDING: Record<string, string> = {
  left: 'pl-6 pr-6 md:pl-12 md:pr-6',
  center: 'px-4 md:px-6',
  right: 'pr-6 pl-6 md:pr-12 md:pl-6',
};

interface HeroBannerSliderProps {
  banners: HeroBannerItem[];
  /** Hauteur personnalisée (utile pour l'aperçu de l'éditeur). */
  className?: string;
  /** Affiche les flèches et pastilles uniquement si plusieurs bannières. */
  autoPlay?: boolean;
}

/**
 * Slider de bannières hero pur (aucun accès base).
 * - 1 bannière  → affichage statique (image + titre + paragraphe).
 * - + de 1     → diaporama automatique avec pastilles et flèches.
 */
export default function HeroBannerSlider({
  banners,
  className,
  autoPlay = true,
}: HeroBannerSliderProps) {
  const [current, setCurrent] = useState(0);
  const count = banners.length;

  useEffect(() => {
    if (!autoPlay || count <= 1) return;
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % count);
    }, 6000);
    return () => clearInterval(timer);
  }, [autoPlay, count]);

  useEffect(() => {
    if (current >= count) setCurrent(0);
  }, [count, current]);

  if (count === 0) return null;

  const go = (idx: number) => setCurrent((idx + count) % count);

  return (
    <section className={`relative w-full overflow-hidden bg-foreground-900 ${className || 'h-[240px] md:h-[360px]'}`}>
      {banners.map((banner, idx) => {
        const active = idx === current;
        const align = banner.textAlign || 'center';
        const hasText = Boolean(banner.title || banner.subtitle || banner.description || banner.ctaLabel);
        const showText = banner.showText !== false && hasText;
        return (
          <div
            key={banner.id}
            className={`absolute inset-0 transition-opacity duration-700 ease-out ${active ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            aria-hidden={!active}
          >
            {banner.image ? (
              <img
                src={banner.image}
                alt={banner.title || 'Bannière'}
                title={banner.title || 'Bannière'}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-foreground-800 to-foreground-950"></div>
            )}

            {/* Voile sombre uniquement quand du texte est affiché :
                sans texte, l'image reste nette et lumineuse. */}
            {showText && (
              <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/25 to-black/45"></div>
            )}

            {showText && (
              <div
                className={`relative h-full w-full flex flex-col justify-center max-w-3xl ${ALIGN_PADDING[align] || ALIGN_PADDING.center} ${ALIGN_CLASS[align] || ALIGN_CLASS.center}`}
              >
                {banner.title && (
                  <h1 className="text-2xl md:text-5xl font-bold font-heading text-white leading-tight">
                    {banner.title}
                  </h1>
                )}
                {banner.subtitle && (
                  <p className="mt-3 text-sm md:text-lg text-white/90 max-w-2xl leading-relaxed">
                    {banner.subtitle}
                  </p>
                )}
                {banner.description && (
                  <p className="mt-2 text-xs md:text-sm text-white/70 max-w-xl leading-relaxed hidden sm:block">
                    {banner.description}
                  </p>
                )}
                {banner.ctaLabel && (
                  <a
                    href={banner.ctaUrl || '#'}
                    className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors no-underline cursor-pointer"
                  >
                    {banner.ctaLabel}
                    <i className="ri-arrow-right-line"></i>
                  </a>
                )}
              </div>
            )}
          </div>
        );
      })}

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(current - 1)}
            aria-label="Bannière précédente"
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/25 text-white backdrop-blur-sm hover:bg-background-50/40 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            onClick={() => go(current + 1)}
            aria-label="Bannière suivante"
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-50/25 text-white backdrop-blur-sm hover:bg-background-50/40 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {banners.map((banner, idx) => (
              <button
                key={banner.id}
                type="button"
                onClick={() => go(idx)}
                aria-label={`Aller à la bannière ${idx + 1}`}
                className={`h-2 rounded-full transition-all cursor-pointer ${idx === current ? 'w-6 bg-background-50' : 'w-2 bg-background-50/50 hover:bg-background-50/80'}`}
              ></button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}